import { useQuery } from "@tanstack/react-query";
import { listVendors, type Vendor } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, ExternalLink } from "lucide-react";

export default function Vendors() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["vendors"],
    queryFn: () => listVendors({ limit: 100 }),
  });

  if (isLoading) {
    return <div className="text-gray-500">Loading vendors...</div>;
  }

  if (error) {
    return <div className="text-red-500">Error loading vendors</div>;
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Vendors</h1>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Add Vendor
        </Button>
      </div>

      <div className="rounded-lg bg-white shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-sm font-medium text-gray-500">
              <th className="px-6 py-3">Name</th>
              <th className="px-6 py-3">Slug</th>
              <th className="px-6 py-3">Website</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Sort Order</th>
              <th className="px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  No vendors found. Create your first vendor to get started.
                </td>
              </tr>
            ) : (
              data?.items.map((vendor: Vendor) => (
                <tr key={vendor.id} className="border-b">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {vendor.logo ? (
                        <img
                          src={`/api/uploads/${vendor.logo}`}
                          alt={vendor.name}
                          className="h-8 w-8 rounded object-cover"
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-sm font-medium">
                          {vendor.name[0]}
                        </div>
                      )}
                      <span className="font-medium">{vendor.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-500">{vendor.slug}</td>
                  <td className="px-6 py-4">
                    {vendor.website ? (
                      <a
                        href={vendor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        Visit
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`rounded-full px-2 py-1 text-xs ${
                        vendor.active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {vendor.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-500">{vendor.sortOrder}</td>
                  <td className="px-6 py-4">
                    <Button variant="ghost" size="sm">
                      Edit
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
